import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/supabase';
import { clearPersistedCache } from '../utils/queryCache';

// Account deletion, required in-app by App Store Review Guideline 5.1.1(v).
//
// The work happens in the delete_my_account() RPC (migration 033): it erases
// the personal fields on the users row and deletes the auth record, leaving an
// anonymous placeholder so the rest of the league keeps its history. The client
// only confirms intent and signs out afterwards.
//
// Two callers: the edit profile modal, where this is a quiet footer link, and
// the public /delete-account page, where Google Play requires the deletion
// pathway to be "prominently featured and easily discoverable". `prominent`
// switches the collapsed trigger to a full-width button and drops the divider.
// It defaults to false, so the modal renders exactly as it always has.

const CONFIRM_WORD = 'DELETE';

const DeleteAccountSection = ({ prominent = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      const { error: rpcError } = await supabase.rpc('delete_my_account');
      if (rpcError) throw rpcError;

      // The auth row is gone, so the session is already dead server side.
      // Signing out clears the local token and the in-memory auth state.
      await supabase.auth.signOut();
      clearPersistedCache();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'We could not delete your account. Try again, or email support.');
      setDeleting(false);
    }
  };

  if (!open) {
    if (prominent) {
      return (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ width: '100%', padding: '14px 0', borderRadius: 10, border: 'none', backgroundColor: 'var(--color-danger-fill)', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
        >
          Delete my account
        </button>
      );
    }
    return (
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-bg-subtle)', textAlign: 'center' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-danger-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Delete account
        </button>
      </div>
    );
  }

  const canDelete = confirmation.trim().toUpperCase() === CONFIRM_WORD && !deleting;

  return (
    <div
      style={
        prominent
          ? { marginTop: 0 }
          : { marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-bg-subtle)' }
      }
    >
      <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-danger-text)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px' }}>
        Delete account
      </h3>

      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--color-text-muted)', margin: '0 0 8px' }}>
        This removes your name, email, date of birth, and profile image, and deletes your sign in
        credentials. It cannot be undone and you will not be able to log back in.
      </p>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--color-text-muted)', margin: '0 0 16px' }}>
        Your leagues keep running without you. Past results stay in the standings under a placeholder
        name so the other managers' history is not broken.
      </p>

      {error && (
        <div style={{ backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: 'var(--color-danger-text)', margin: 0 }}>{error}</p>
        </div>
      )}

      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: 6 }}>
        Type {CONFIRM_WORD} to confirm
      </label>
      <input
        type="text"
        value={confirmation}
        onChange={e => setConfirmation(e.target.value)}
        placeholder={CONFIRM_WORD}
        autoComplete="off"
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--color-border)', borderRadius: 8, fontSize: 14, color: 'var(--color-text-strong)', backgroundColor: 'var(--color-bg-page)', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button
          type="button"
          onClick={() => { setOpen(false); setConfirmation(''); setError(''); }}
          disabled={deleting}
          style={{ padding: '11px 0', borderRadius: 10, border: '1.5px solid var(--color-border)', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-muted)', fontWeight: 600, fontSize: 14, cursor: deleting ? 'not-allowed' : 'pointer' }}
        >
          Keep my account
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!canDelete}
          style={{ padding: '11px 0', borderRadius: 10, border: 'none', backgroundColor: canDelete ? 'var(--color-danger-fill)' : 'var(--color-border)', color: canDelete ? '#fff' : 'var(--color-text-faint)', fontWeight: 600, fontSize: 14, cursor: canDelete ? 'pointer' : 'not-allowed' }}
        >
          {deleting ? 'Deleting...' : 'Delete forever'}
        </button>
      </div>
    </div>
  );
};

export default DeleteAccountSection;
